import React from 'react';
import { filter, sortBy } from 'lodash';
import { matchPath } from 'react-router-dom';

import { ReactComponent as HomeIcon } from '../assetsNew/icons/menu/home.svg';
import { ReactComponent as PerformanceIcon } from '../assetsNew/icons/menu/performance.svg';
import { ReactComponent as CampaignsIcon } from '../assetsNew/icons/menu/campaigns.svg';
import { ReactComponent as PromotionsIcon } from '../assetsNew/icons/menu/promotions.svg';
import { ReactComponent as ChatBubbleIcon } from '../assetsNew/icons/menu/chat-bubble-left.svg';
import { ReactComponent as GiftsIcon } from '../assetsNew/icons/menu/gifts.svg';
import { ReactComponent as DiscountsIcon } from '../assetsNew/icons/menu/discounts.svg';
import { ReactComponent as DiscoverIcon } from '../assetsNew/icons/menu/discover.svg';
import { ReactComponent as MyCreatorsIcon } from '../assetsNew/icons/menu/my-creators.svg';
import { ReactComponent as BillingIcon } from '../assetsNew/icons/menu/billing.svg';
import { ReactComponent as InviteIcon } from '../assetsNew/icons/menu/invite.svg';
// import { ReactComponent as ExportsIcon } from '../assetsNew/icons/menu/exports.svg';
import { ReactComponent as MerchantsIcon } from '../assetsNew/icons/menu/merchants.svg';
import { ReactComponent as CommissionsIcon } from '../assetsNew/icons/menu/commissions.svg';
import { ReactComponent as MembersIcon } from '../assetsNew/icons/menu/members.svg';
import { ReactComponent as ListIcon } from '../assetsNew/icons/menu/list.svg';
import { ReactComponent as InvitationsIcon } from '../assetsNew/icons/menu/invitations.svg';
import { ReactComponent as PaymentsIcon } from '../assetsNew/icons/menu/payments.svg';
import { ReactComponent as OrgDetailsIcon } from '../assetsNew/icons/menu/org-details.svg';
import { ReactComponent as AccountInfoIcon } from '../assetsNew/icons/menu/account-info.svg';
import { ReactComponent as HelpIcon } from '../assetsNew/icons/menu/help.svg';

import { routes } from '../pages';
// import { ENVIRONMENT } from '../config';

export const menuSections = (isAdmin = true) => [
  {
    items: [
      { id: 0, title: 'Home', path: routes.root.path, icon: <HomeIcon /> },
      { id: 10, title: 'Performance', path: routes.performance.path, icon: <PerformanceIcon /> },
      { id: 20, title: 'Messages', path: routes.chat.root.path, icon: <ChatBubbleIcon />, unreadBadge: true }
    ]
  },
  {
    title: 'Growth Tools',
    items: [
      { id: 30, title: 'Bonuses', path: routes.campaigns.list.path, icon: <CampaignsIcon /> },
      { id: 40, title: 'Gifts', path: routes.gifts.root.path, icon: <GiftsIcon /> },
      { id: 50, title: 'Promotions', path: routes.promotions.root.path, icon: <PromotionsIcon /> },
      { id: 60, title: 'Discounts', path: routes.discounts.root.path, icon: <DiscountsIcon /> }
    ]
  },
  {
    title: 'Manage Creators',
    items: [
      { id: 70, title: 'Discover', path: routes.discover.root.path, icon: <DiscoverIcon /> },
      { id: 80, title: 'My creators', path: routes.myCreators.root.path, icon: <MyCreatorsIcon /> },
      { id: 90, title: 'Invite', path: routes.refer.root.path, icon: <InviteIcon /> }
    ]
  },
  {
    title: 'Other',
    items: [
      { id: 65, title: 'Affiliate Network', path: routes.affiliateNetwork.root.path, icon: <CommissionsIcon /> },
      ...(isAdmin ? [{ id: 100, title: 'Billing & balance', path: routes.billing.root.path, icon: <BillingIcon /> }] : [])
      // { id: 110, title: 'Data Exports', path: routes.dataExports.path, icon: <ExportsIcon /> }
    ]
  }
].filter(section => section.items.length > 0);

export const menuItems = (isAdmin = true) => menuSections(isAdmin).flatMap(section => section.items);

export const settingsItems = isAdmin => {
  const items = [
    { id: 120, title: 'Merchants', path: routes.merchants.root.path, icon: <MerchantsIcon /> },
    { id: 130, title: 'Team Members', path: '', icon: <MembersIcon />, more: [
        { id: 130.1, title: 'List', path: routes.members.list.path, icon: <ListIcon /> },
        { id: 130.2, title: 'Invitations', path: routes.members.invitations.path, icon: <InvitationsIcon /> }
      ] },
    { id: 140, title: 'Org Details', path: routes.organization.root.path, icon: <OrgDetailsIcon /> },
    { id: 160, title: 'Account Info', path: routes.account.root.path, icon: <AccountInfoIcon /> },
    { id: 170, title: 'Help', path: routes.help.path, icon: <HelpIcon /> }
  ];

  return isAdmin ? sortBy([...items, { id: 150, title: 'Payments', icon: <PaymentsIcon /> } ], ['id']) : items;
};

export const subMenuObjects = isAdmin => {
  return filter([...menuItems(), ...settingsItems(isAdmin)], item => item.more).reduce((acc, item) => {
    let { id } = item;
    return { ...acc, [id]: false };
  }, {});
};

const flattenMenuItems = items => items.reduce((acc, item) => {
  const currentItems = item.more ? [item, ...item.more] : [item];
  return [...acc, ...currentItems];
}, []);

// Menu paths (matched exactly) where the AI assistant is available.
export const aiAssistantMenuPaths = (() => {
  const allMenuEntries = [
    ...menuItems(),
    ...flattenMenuItems(settingsItems(true)),
    ...flattenMenuItems(settingsItems(false))
  ];

  return Array.from(new Set(allMenuEntries
    .map(item => item.path)
    .filter(path => Boolean(path) && path !== routes.help.path)));
})();

// Matches the pathname against route patterns (with :params), also covering their nested subroutes.
// The root route is matched exactly - otherwise it would be a prefix of every path.
const matchesAnyRoute = (pathname, routeLinks) => Boolean(pathname) && routeLinks.some(routeLink => Boolean(matchPath(pathname, {
  path: routeLink.path,
  exact: routeLink.path === routes.root.path
})));

// Form routes with a sticky bottom action bar - the round launcher is moved up so it does not cover it.
export const aiAssistantRaisedLauncherRoutes = [
  routes.campaigns.create,
  routes.campaigns.edit,
  routes.codes.create,
  routes.codes.edit,
  routes.codes.duplicate,
  routes.gifts.create,
  routes.gifts.edit,
  routes.gifts.duplicate,
  routes.discounts.create,
  routes.discounts.edit,
  routes.discounts.duplicate,
  routes.reimbursement.create,
  ...Object.values(routes.lookbook.create),
  ...Object.values(routes.lookbook.edit),
  routes.lookbook.inventory
];

export const isAiAssistantRaisedLauncherPath = pathname => matchesAnyRoute(pathname, aiAssistantRaisedLauncherRoutes);

// Routes where the round launcher is replaced by the AIAssistantBar, which opens the assistant in floating mode.
export const aiAssistantBarRoutes = [
  routes.root,
  ...Object.values(routes.creator.root),
  ...Object.values(routes.creator.similarCreators),
  routes.discover.root
];

export const isAiAssistantBarPath = pathname => matchesAnyRoute(pathname, aiAssistantBarRoutes);

// Routes where the AI assistant is embedded in a side panel (the Chat creator panel) - the round launcher is hidden there.
export const aiAssistantPanelRoutes = [
  routes.chat.root
];

export const isAiAssistantPanelPath = pathname => matchesAnyRoute(pathname, aiAssistantPanelRoutes);

// The creator panel (and so the hidden launcher) needs room next to the channel list and the channel.
export const AI_ASSISTANT_PANEL_MEDIA_QUERY = '(min-width:1330px)';

// Routes outside the menu (with :params) where the AI assistant is available.
export const aiAssistantNonMenuRoutes = [
  ...aiAssistantBarRoutes,
  ...aiAssistantRaisedLauncherRoutes,
  routes.campaigns.summary,
  routes.lookbook.preview,
  routes.lookbook.summary
];

export const isAiAssistantVisiblePath = pathname => {
  if (!pathname) {
    return false;
  }

  const normalizedPathname = pathname.replace(/\/$/, '') || '/';

  if (matchesAnyRoute(normalizedPathname, aiAssistantNonMenuRoutes)) {
    return true;
  }

  return aiAssistantMenuPaths.some(path => {
    const normalizedPath = path.replace(/\/$/, '') || '/';
    return normalizedPathname === normalizedPath;
  });
};
